/**
 * Step 6 — which stores and streaming services a release goes to.
 *
 * ============================================================================
 * THE PLATFORM LIST IS NOT WRITTEN HERE, AND THAT IS §13's CENTRAL REQUIREMENT.
 *
 * "Do NOT hard-code the list of platforms as the source of truth… Do not claim that
 * Spotify, Apple Music, YouTube Music, Amazon Music, TikTok, Instagram/Facebook,
 * Deezer, Tidal, etc. are supported unless the actual LabelGrid outlet data confirms
 * it."
 *
 * So this component renders the catalogue it is given. Real mode uses
 * `GET /distro-outlets`; prototype mode uses the public LabelGrid supported-store
 * directory so the release workflow remains testable before account API access exists.
 *
 * There is also a real reason beyond honesty: the outlet catalogue is
 * ACCOUNT-SPECIFIC. Which outlets a label can deliver to depends on its LabelGrid
 * configuration, so there is no correct static list to hard-code even in principle.
 * ============================================================================
 *
 * `is_ai_dsp` is surfaced rather than hidden. It is LabelGrid telling us an outlet
 * specifically handles AI-generated music, which is directly material to a catalogue that
 * is entirely model-generated — some stores treat AI content differently, and a user
 * choosing where to send a Lyrixa track should be able to see which ones those are.
 */
import { Bot, Check, Radio } from 'lucide-react';
import type { LabelGridOutlet } from '../../api/labelgrid';
import {
  isPublisherOutletSectionAvailable,
  publisherOutletRequiresUnlimited,
} from '../../data/publisherOutlets';
import { c } from '../../styles/theme';
import { Tooltip } from '../ui';
import { PublisherOutletIcon } from './PublisherOutletGlyphs';

export interface OutletPickerProps {
  /** From publisherApi.getOutlets(), with the prototype catalogue as its empty fallback. */
  outlets: LabelGridOutlet[];
  /** Selected outlet keys — LabelGrid `distro_outlet_id` slugs. */
  selected: readonly string[];
  onToggle: (outletKey: string) => void;
  hasUnlimitedPlan: boolean;
  /** True while the catalogue is being fetched. */
  loading?: boolean;
  /**
   * Why the catalogue is empty, when it is.
   *
   * Passed in rather than written here so the copy comes from the API boundary's own
   * blockers and cannot drift from what the code checks.
   */
  unavailableReason?: string;
}

export default function OutletPicker({
  outlets,
  selected,
  onToggle,
  hasUnlimitedPlan,
  loading,
  unavailableReason,
}: OutletPickerProps) {
  if (loading) {
    // An honest loading line, not a percentage. §34: no fake progress where real
    // progress is unavailable, and a catalogue fetch has no progress to report.
    return <p style={{ fontSize: 12, color: c.text2, margin: 0 }}>Loading the outlet catalogue…</p>;
  }

  if (outlets.length === 0) {
    return (
      <div
        style={{
          border: `1px solid ${c.border}`,
          background: c.surface2,
          borderRadius: 10,
          padding: '14px 16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
          <Radio size={15} color={c.text2} style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ minWidth: 0 }}>
            <p style={{ margin: '0 0 6px', fontSize: 12.5, color: c.text1, fontWeight: 500 }}>
              No outlets can be listed yet
            </p>
            <p style={{ margin: 0, fontSize: 11.5, color: c.text2, lineHeight: 1.55 }}>
              {/* The reason, then the rule. Naming the endpoint is deliberate: it is
                  checkable, and it makes clear this is a missing connection rather than
                  an unbuilt screen. */}
              {unavailableReason ??
                'The store and streaming-service list comes from LabelGrid’s own outlet catalogue, ' +
                  'and this build cannot read it.'}{' '}
              The catalogue is account-specific — which services a label can deliver to depends on
              its LabelGrid configuration — so Lyrixsa does not ship a list of its own. Showing
              Spotify, Apple Music or TikTok here without confirming them against{' '}
              <span style={{ fontFamily: 'IBM Plex Mono' }}>GET /distro-outlets</span> would be
              claiming support that has not been verified.
            </p>
          </div>
        </div>
      </div>
    );
  }

  /*
    Grouped by the outlet's own `type`.

    LabelGrid's grouping rather than one this app invented: `type` is a documented field
    and it is how the catalogue describes itself. Sorted by name inside each group so the
    order is stable across reloads.
  */
  const groups = new Map<string, LabelGridOutlet[]>();
  for (const outlet of outlets.filter((item) =>
    isPublisherOutletSectionAvailable(item, hasUnlimitedPlan)
  )) {
    const key = outlet.type || 'Other';
    const list = groups.get(key);
    if (list) list.push(outlet);
    else groups.set(key, [outlet]);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {[...groups.entries()].map(([type, list]) => (
        <div key={type}>
          <p className="lyx-ws-eyebrow" style={{ margin: '0 0 7px' }}>
            {type}
          </p>
          <div
            role="group"
            aria-label={type}
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: 8,
            }}
          >
            {[...list]
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((outlet) => {
                const checked = selected.includes(outlet.key);
                const requiresUnlimited =
                  !hasUnlimitedPlan && publisherOutletRequiresUnlimited(outlet);
                const tile = (
                  <label
                    key={outlet.id}
                    className="lyx-pick"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      border: `1px solid ${checked ? c.accentBorder : c.border}`,
                      background: checked ? c.accentSoft : c.surface2,
                      borderRadius: 8,
                      padding: '11px 12px',
                      minHeight: 48,
                      width: '100%',
                      boxSizing: 'border-box',
                      cursor: requiresUnlimited ? 'not-allowed' : 'pointer',
                      opacity: requiresUnlimited ? 0.55 : 1,
                      boxShadow: checked ? `inset 0 0 0 1px ${c.accentBorder}` : 'none',
                    }}
                  >
                    <input
                      type="checkbox"
                      className="lyx-visually-hidden"
                      checked={checked}
                      onChange={() => onToggle(outlet.key)}
                      disabled={requiresUnlimited}
                      aria-label={`${checked ? 'Remove' : 'Add'} ${outlet.name}`}
                    />
                    <span
                      style={{
                        width: 30,
                        height: 30,
                        borderRadius: 6,
                        background: checked ? c.surface1 : c.surface3,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <PublisherOutletIcon
                        outletKey={outlet.key}
                        outletName={outlet.name}
                        size={19}
                      />
                    </span>
                    <span
                      style={{
                        minWidth: 0,
                        flex: 1,
                        fontSize: 12,
                        lineHeight: 1.3,
                        color: c.text1,
                      }}
                    >
                      {outlet.name}
                    </span>
                    {checked && <Check size={15} color={c.accent} style={{ flexShrink: 0 }} />}
                    {outlet.is_ai_dsp && (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          flexShrink: 0,
                          fontSize: 10.5,
                          color: c.accent,
                        }}
                      >
                        <Bot size={11} />
                        AI music
                      </span>
                    )}
                  </label>
                );
                return requiresUnlimited ? (
                  <Tooltip
                    key={outlet.id}
                    label="Unlimited package required"
                    describe
                    style={{ width: '100%' }}
                  >
                    {tile}
                  </Tooltip>
                ) : (
                  tile
                );
              })}
          </div>
        </div>
      ))}
    </div>
  );
}
